import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { GetStringValue } from "../../utilities/utilities";
import { ITemplate } from "../../types/types";
import { AlertType } from "../../types/enums";
import { getTemplateDetailAsync, checkTemplateGenerationCompleteAsync } from "./template.api";
import useTemplateGenerationPolling, { TemplateGenerationPollStatus } from "../../hooks/useTemplateGenerationPolling";
import { TEMPLATE_REGENERATION_FAILED_MESSAGE, TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE, TEMPLATE_REGENERATION_TIMEOUT_MESSAGE } from "../../constants/CommonConstants";
import { showAlert } from "../Alerts/alerts.actions";

const useStyles = makeStyles((theme: Theme) => ({
	button: {
		background: "#006B86",
		size: "small",
		margin: theme.spacing(1),
		color: "white",
	}
}));

interface NewTemplateDialogProps {
	open: boolean;
	onSave: (newTemplate: ITemplate) => void;
	onClose: () => void;
	copyTemplateId: number | null;
}

const NewTemplateDialog = ({ open, onSave, onClose, copyTemplateId }: NewTemplateDialogProps) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [templateNameError, setTemplateNameError] = useState('');
	const [canSave, setCanSave] = useState(false);
	const [newTemplateName, setNewTemplateName] = useState('');
	const [templateExistsMessage, setTemplateExistsMessage] = useState('This template name is already used. Please enter another name.');
	const [isWaitingForRegeneration, setIsWaitingForRegeneration] = useState(false);
	const [regenerationBanner, setRegenerationBanner] = useState<{ severity: AlertType; message: string } | null>(null);
	const regenerationResolveRef = useRef<((status: TemplateGenerationPollStatus) => void) | null>(null);

	const { startPolling: startRegenerationPolling } = useTemplateGenerationPolling({
		checkCompletion: checkTemplateGenerationCompleteAsync,
		onCompleted: () => regenerationResolveRef.current?.('completed'),
		onTimedOut: () => regenerationResolveRef.current?.('timedOut'),
		onError: () => regenerationResolveRef.current?.('failed'),
	});

	const waitForRegeneration = useCallback((newTemplateId: number): Promise<TemplateGenerationPollStatus> => {
		return new Promise<TemplateGenerationPollStatus>((resolve) => {
			regenerationResolveRef.current = resolve;
			startRegenerationPolling(newTemplateId);
		});
	}, [startRegenerationPolling]);

	useEffect(() => {
		if (open) {
			setRegenerationBanner(null);
		}
	}, [open]);

	useEffect(() => {
		const prepTemplateCopy = async () => {
			let templateCopyName = await getTemplateName(copyTemplateId);
			setNewTemplateName(`${templateCopyName} - Copy`);
			setCanSave(true);
		};

		if (copyTemplateId) {
			prepTemplateCopy();
		}
	}, [copyTemplateId]);

	useEffect(() => {
		const getErrorMessage = async () => {
			const theString = await GetStringValue("templateNameAlreadyUsed");

			if (theString) {
				setTemplateExistsMessage(theString.value || templateExistsMessage);
			}
		};

		getErrorMessage();
	}, []);

	const getTemplateName = async (templateId?: number | null): Promise<string | null> => {
		console.log(`Getting template name for template ID ${templateId}...`);
		let template = await getTemplateDetailAsync(templateId ?? 0);
		return template.name;
	}

	const resetState = () => {
		setNewTemplateName('');
		setCanSave(false);
		setTemplateNameError('');
	}

	const handleClose = (event?: React.MouseEventHandler<HTMLButtonElement> | any, reason?: string) => {
		if (reason === 'backdropClick' || isWaitingForRegeneration) {
			return;
		}

		resetState();
		onClose();
	};

	const handleTemplateNameValidation = () => {
		if (templateNameError)
			setTemplateNameError('');

		if (newTemplateName && newTemplateName.trim().length > 0) {
			setCanSave(true);
		} else {
			setCanSave(false);
		}
	};

	const saveTemplate = (templateName: string) => {
		setRegenerationBanner(null);
		fetch('api/template/createTemplate', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ templateName: newTemplateName, copyTemplateId: copyTemplateId }),
		})
			.then(response => response.json())
			.then(async data => {
				if (data) {
					if (data.error) {
						setCanSave(false);
						setTemplateNameError(data.error);
						return;
					}

					const newTemplate = data as ITemplate;

					let proceed = true;

						try {
							setIsWaitingForRegeneration(true);
							const status = await waitForRegeneration(newTemplate.id);

							if (status === 'timedOut') {
								dispatch(showAlert({
									hidden: false,
									severity: AlertType.warning,
									message: TEMPLATE_REGENERATION_TIMEOUT_MESSAGE
								}));
								setRegenerationBanner({ severity: AlertType.warning, message: TEMPLATE_REGENERATION_TIMEOUT_MESSAGE });
								proceed = false;
							} else if (status === 'failed') {
								dispatch(showAlert({
									hidden: false,
									severity: AlertType.error,
									message: TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE
								}));
								setRegenerationBanner({ severity: AlertType.error, message: TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE });
								proceed = false;
							} else {
								setRegenerationBanner(null);
							}
						} catch (error) {
							console.error('Error:', error);
							dispatch(showAlert({
								hidden: false,
								severity: AlertType.error,
								message: TEMPLATE_REGENERATION_FAILED_MESSAGE
							}));
							setRegenerationBanner({ severity: AlertType.error, message: TEMPLATE_REGENERATION_FAILED_MESSAGE });
							proceed = false;
						} finally {
							setIsWaitingForRegeneration(false);
						}

						if (!proceed) {
							return;
						}

						resetState();
						onSave(newTemplate);
				} else {
					setCanSave(false);
					setTemplateNameError("Nothing returned from the server.  If this continues, please contact support.");
				}
			})
			.catch((error) => {
				console.error('Error:', error);
				setCanSave(false);
				setTemplateNameError(error);
			});
	};

	const handleSave = (e: { preventDefault: () => void; }) => {
		e.preventDefault();

		// call API to check if template name already exists

		fetch('api/Template/CheckTemplateName', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ templateName: newTemplateName }),
		})
			.then(response => response.json())
			.then(data => {
				if (data) {
					if (data.found) {
						setCanSave(false);
						setTemplateNameError(templateExistsMessage);
						return;
					}

					if (data.error) {
						setCanSave(false);
						setTemplateNameError(data.error);
						return;
					}

					saveTemplate(newTemplateName);
				} else {
					setCanSave(false);
					setTemplateNameError(templateExistsMessage);
				}
			})
			.catch((error) => {
				console.error('Error:', error);
				setCanSave(false);
				setTemplateNameError(error);
			});
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth={false} PaperProps={{ style: { width: '32rem' } }}>
			<DialogTitle>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
					<span>Create New Template</span>
					<Button onClick={handleClose} disabled={isWaitingForRegeneration} style={{ border: 'none', minWidth: '30px', color: 'gray' }}>X</Button>
				</div>
			</DialogTitle>

			<DialogContent>
				{regenerationBanner && (
					<Alert
						severity={regenerationBanner.severity}
						onClose={() => setRegenerationBanner(null)}
						sx={{ mb: 2 }}
					>
						{regenerationBanner.message}
					</Alert>
				)}
				<TextField
					autoFocus
					margin="normal"
					label="New Template Name"
					fullWidth
					value={newTemplateName}
					onChange={(e) => setNewTemplateName(e.target.value)}
					variant="standard"
					onKeyUp={handleTemplateNameValidation}
					error={!!templateNameError}
					helperText={templateNameError}
					InputLabelProps={{
						shrink: true,
						style: {
							color: "black",
						}
					}}
				/>
			</DialogContent>

			<DialogActions>
				<Button variant="outlined" onClick={handleClose} disabled={isWaitingForRegeneration}>Cancel</Button>
				<Button
					className={classes.button}
					variant="contained"
					onClick={handleSave}
					disabled={!canSave || isWaitingForRegeneration || regenerationBanner?.severity === AlertType.error}
					startIcon={isWaitingForRegeneration ? <CircularProgress size={16} color="inherit" /> : undefined}
					sx={{
						"&:focus": {
						backgroundColor: "#253746 !important", // Keep the default background color on focus
						},
					}}>
					{isWaitingForRegeneration ? 'Generating...' : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default NewTemplateDialog;